import { html, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { styleMap } from 'lit/directives/style-map.js'
import { Focusable } from '../common/mixins/Focusable'
import { Compact } from '../common/mixins/Compact'
import { Disabled } from '../common/mixins/Disabled'
import { FormAssociatedFactory } from '../common/mixins/FormAssociated'
import { Label } from '../common/mixins/Label'
import { Implicit } from '../common/mixins/Implicit'
import { Purpose } from '../common/mixins/Purpose'
import { StyledFactory } from '../common/mixins/Styled'
import { ValueFactory } from '../common/mixins/Value'
import { OneUxElement } from '../common/OneUxElement'
import { log } from '../common/utils/log'
import { Axis } from './components/Axis'
import { getLanguage } from './lang'
import { style } from './style'
import { Step } from './types'

const Styled = StyledFactory(style)

const BaseClass = Compact(Disabled(Focusable(Implicit(Label(Purpose(Styled(OneUxElement)))))))

type valueType = unknown[]
const Value = ValueFactory<valueType>({
  type: Array,
  reflect: true
})

const FormAssociated = FormAssociatedFactory<valueType>()

/**
 * A slider supporting single or multiple thumbs, for selecting a single value or a range.
 *
 * $$preview$$
 */
@customElement('one-ux-slider')
export class OneUxSliderElement extends FormAssociated(Value(BaseClass)) {
  /*
   * Toggles on a second thumb to select a range as value.
   */
  @property({ type: Boolean })
  public range = false

  /*
   * The list of steps for the slider.
   * * text: Displayed text for option
   * * value: When listening on the input event it will be passed as argument
   * * tick: toggles display as tick on the axis
   */
  @property({ type: Array })
  public steps!: Step[]

  #refs = {
    $fields: createRef(),
    $start: createRef<HTMLInputElement>(),
    $end: createRef<HTMLInputElement>()
  }

  #min = 0
  #valueObject: Step[] = []

  protected willUpdate(): void {
    if (!this.value) {
      if (this.range) {
        this.value = [this.steps[this.#min].value, this.steps[this.#max].value]
      } else {
        this.value = [this.steps[this.#min].value]
      }
    }

    this.#valueObject = this.range
      ? [this.steps[this.#position('start')], this.steps[this.#position('end')]]
      : [this.steps[this.#position('start')]]
  }

  render() {
    if (!this.steps) {
      log.error({ title: '<one-ux-slider> Missing steps, not rendering.', details: this })
      return
    }

    const language = getLanguage(this)
    const valueText = this.range ? this.#valueObject.map(({ text }) => text).join('–') : this.#valueObject[0].text
    const startPosition = this.#position('start')
    const endPosition = this.#position('end')

    return html`<div
      class="one-ux-element--root main"
      role=${ifDefined(this.range ? 'group' : undefined)}
      aria-label=${ifDefined(this.range ? this.label : undefined)}
      style=${styleMap({
        '--a': this.range ? startPosition : this.#min,
        '--b': this.range ? endPosition : startPosition,
        '--min': this.#min,
        '--max': this.#max
      })}
    >
      ${this.compact
        ? html`<one-ux-tooltip placement="above" id="tooltip"> ${this.label}: ${valueText} </one-ux-tooltip>`
        : html`
            <div class="summary">
              ${this.label}:
              <span> ${valueText} </span>
            </div>
          `}
      <div ${ref(this.#refs.$fields)} class="fields" one-ux-tooltip=${ifDefined(this.compact ? '#tooltip' : undefined)}>
        <input
          ${ref(this.#refs.$start)}
          type="range"
          ?disabled=${this.disabled}
          min=${this.#min}
          max=${this.#max}
          value=${startPosition}
          aria-label=${this.range ? language.translations.start : this.label}
          aria-valuenow=${String(this.#valueObject[0].value)}
          aria-valuetext=${this.#valueObject[0].text}
          @change=${this.#handleChange}
          @input=${this.#handleInput}
          @mousedown=${this.#handleMouseDown}
          @mouseup=${this.#handleMouseUp}
        />
        ${this.range
          ? html`<input
              ${ref(this.#refs.$end)}
              type="range"
              ?disabled=${this.disabled}
              min=${this.#min}
              max=${this.#max}
              value=${endPosition}
              aria-label=${language.translations.end}
              aria-valuenow=${String(this.#valueObject[1].value)}
              aria-valuetext=${this.#valueObject[1].text}
              @change=${this.#handleChange}
              @input=${this.#handleInput}
              @mousedown=${this.#handleMouseDown}
              @mouseup=${this.#handleMouseUp}
            />`
          : nothing}
      </div>
      ${Axis(this.steps)}
      ${this.implicit
        ? nothing
        : html`<div class="ruler">
            <div class="min">${this.steps[0].text}</div>
            <div class="max">${this.steps[this.steps.length - 1].text}</div>
          </div> `}
    </div>`
  }

  #handleInput(event: InputEvent) {
    event.stopPropagation()
    const $slider = event.target as HTMLInputElement
    const newValue = this.steps[Number($slider.value)].value

    if (this.range) {
      const [start, end] = this.value!

      if (event.target === this.#refs.$start.value) {
        this.value = [newValue, end]
      } else {
        this.value = [start, newValue]
      }

      this.#refs.$start.value!.style.zIndex = event.target === this.#refs.$start.value ? '2' : '1'
      this.#refs.$end.value!.style.zIndex = event.target === this.#refs.$end.value ? '2' : '1'
    } else {
      this.value = [newValue]
    }
    this.dispatchEvent(new Event('input'))
  }

  #handleChange(event: InputEvent) {
    event.stopPropagation()
    this.dispatchEvent(new Event('change'))
  }

  #handleMouseDown() {
    this.#refs.$fields.value!.classList.add('active')
  }

  #handleMouseUp() {
    this.#refs.$fields.value!.classList.remove('active')
  }

  #position(type: 'start' | 'end') {
    const index = type === 'end' ? 1 : 0
    return this.steps.findIndex(({ value }) => this.value![index] == value)
  }

  get #max() {
    return this.steps.length - 1
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'one-ux-slider': OneUxSliderElement
  }

  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'one-ux-slider': OneUxSliderElement
    }
  }
}
